import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { REPORTS_INFORMES_LIST_PAGE_SIZE, type ReportsSection } from './reportsScreen.constants';
import { shouldResetReportsScopeOnBranchChange } from './reportsBranchChange';
import { useReportsNavigationPane } from './hooks/useReportsNavigationPane';
import { useReportsVentasListPane } from './hooks/useReportsVentasListPane';
import { useReportsOperacionPane } from './hooks/useReportsOperacionPane';
import { useReportsComprasPane } from './hooks/useReportsComprasPane';
import { useReportsStockMerma } from './hooks/useReportsStockMerma';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { planAllowsCustomer } from '../../shell/feature-flags/planEntitlementsService';
import { showError } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { exportStockMermaCsv } from './reportsCsvExport';
import { getReportsDateRangeLabel } from './reportsScreen.utils';
import type { ReportsCustomRange } from './reportsScreen.utils';
import { setReportsNavigationIntent } from './reportsNavigationIntent';

/** Modo operación: con conexión, ventas/caja agregadas desde Supabase (RPC); sin conexión o si falla el remoto, SQLite local. */
export const useReportsScreen = () => {
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branchesLoading = useSessionStore((state) => state.branchesLoading);
  const user = useAuthStore((state) => state.user);
  const { canEdit, canView } = useBranchPermission('Reports');
  const canSales = useModulePermission('Reports', 'sales');
  const canCash = useModulePermission('Reports', 'cash');
  const canExpenses = useModulePermission('Reports', 'expenses');
  const canInventory = useModulePermission('Reports', 'inventory');
  const canCustomer = useModulePermission('Reports', 'fiado');
  const canMargins = useModulePermission('Reports', 'margins');
  const canByEmployee = useModulePermission('Reports', 'by_employee');
  const canPurchases = useModulePermission('Reports', 'purchases');
  const canAppointments = useModulePermission('Reports', 'appointments');
  const canExport = useModulePermission('Reports', 'export');
  const { entitlements, loading: entitlementsLoading } = usePlanEntitlements();
  const { flags: globalFeatureFlags, loading: globalFeatureFlagsLoading } = useGlobalFeatureFlags();

  const reportsBootstrapReady = !entitlementsLoading && !globalFeatureFlagsLoading;

  const allowedSections = useMemo((): ReportsSection[] => {
    if (!canView) return [];
    const out: ReportsSection[] = ['resumen'];
    if (canSales || canMargins) out.push('ventas');
    if (canByEmployee) out.push('personal');
    if (canCash || canExpenses || canInventory || canCustomer) out.push('operacion');
    if (canPurchases) out.push('compras');
    if (canAppointments) out.push('agenda');
    return out;
  }, [canView, canSales, canMargins, canByEmployee, canCash, canExpenses, canInventory, canCustomer, canPurchases, canAppointments]);

  const mercadopagoQrMethodAllowed = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'mercadopago_integrations')) return false;
    if (!entitlements) return true;
    return entitlements.mp_qr_pos === true;
  }, [globalFeatureFlags?.mercadopago_integrations, entitlements?.mp_qr_pos]);

  const customerModuleEnabled = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'fiado_integrations')) return false;
    return planAllowsCustomer(entitlements);
  }, [
    globalFeatureFlags?.fiado_integrations,
    entitlements?.fiado_manual_only,
    entitlements?.fiado_payment_link,
  ]);

  const navigationPane = useReportsNavigationPane();

  const ventasPane = useReportsVentasListPane({
    navigation: navigationPane.navigation,
    currentBranchId,
    isVentasSection: navigationPane.isVentasSection,
    isFocused: navigationPane.isFocused,
    reportsBootstrapReady,
    dateRangePreset: navigationPane.dateRangePreset,
    customRangeFrom: navigationPane.customRangeFrom,
    customRangeTo: navigationPane.customRangeTo,
    customRangeParam: navigationPane.customRangeParam,
  });

  const { salesByProduct } = ventasPane;

  const comprasPane = useReportsComprasPane({
    currentBranchId,
    dateRangePreset: navigationPane.dateRangePreset,
    customRangeFrom: navigationPane.customRangeFrom,
    customRangeTo: navigationPane.customRangeTo,
    shouldLoad: navigationPane.activeSection === 'compras' && canPurchases,
  });

  const operacionPane = useReportsOperacionPane({
    currentBranchId,
    userId: user?.id,
    canEdit,
    canExport,
    dateRangePreset: navigationPane.dateRangePreset,
    customRangeFrom: navigationPane.customRangeFrom,
    customRangeTo: navigationPane.customRangeTo,
    reportsQueryScope: ventasPane.reportsQueryScope,
    customerModuleEnabled,
    mercadopagoQrMethodAllowed,
    shouldLoadOperacionDashboard: navigationPane.shouldLoadOperacionDashboard,
    shouldLoadIntelligence: navigationPane.shouldLoadIntelligence,
    shouldLoadPersonalDashboard: navigationPane.shouldLoadPersonalDashboard,
    shouldLoadTreasurySummary: navigationPane.shouldLoadTreasurySummary,
    reportsBootstrapReady,
    activeSection: navigationPane.activeSection,
    salesByProductCategory: ventasPane.salesByProductCategory,
    salesByProductItemTypeFilter: ventasPane.salesByProductItemTypeFilter,
    reportsVentasPinnedProductId: ventasPane.reportsVentasPinnedProductId,
    reportsVentasPinnedProductLabel: ventasPane.reportsVentasPinnedProductLabel,
    salesByProductSort: salesByProduct.salesByProductSort,
    salesByProductRows: salesByProduct.salesByProductRows,
    salesByProductPage: salesByProduct.salesByProductPage,
    salesByProductPageSize: salesByProduct.salesByProductPageSize,
    salesByProductTotalCount: salesByProduct.salesByProductTotalCount,
    salesByProductSumQuantity: salesByProduct.salesByProductSumQuantity,
    salesByProductSumLineTotal: salesByProduct.salesByProductSumLineTotal,
    salesByProductUniformUnitOfMeasure: salesByProduct.salesByProductUniformUnitOfMeasure,
    bumpSalesByProductRefreshKey: ventasPane.bumpSalesByProductRefreshKey,
    purchasesSummary: comprasPane.purchasesSummary,
    supplierPerformance: comprasPane.supplierPerformance,
  });

  const [stockMermaRefreshKey, setStockMermaRefreshKey] = useState(0);
  const stockMerma = useReportsStockMerma({
    currentBranchId,
    isOperacionSection: navigationPane.isOperacionSection,
    isFocused: navigationPane.isFocused,
    reportsBootstrapReady,
    canInventory,
    dateRangePreset: navigationPane.dateRangePreset,
    customRangeFrom: navigationPane.customRangeFrom,
    customRangeTo: navigationPane.customRangeTo,
    refreshKey: stockMermaRefreshKey,
  });

  const handleShareStockMerma = useCallback(async () => {
    if (!canExport) {
      showError('Sin permisos', 'Tu perfil no puede exportar informes.');
      return;
    }
    const customParam: ReportsCustomRange | null =
      navigationPane.dateRangePreset === 'custom'
        ? { from: navigationPane.customRangeFrom, to: navigationPane.customRangeTo }
        : null;
    const label = getReportsDateRangeLabel(navigationPane.dateRangePreset, customParam);
    try {
      await exportStockMermaCsv({
        periodLabel: label,
        rows: stockMerma.stockMermaRows,
        sumPurchased: stockMerma.stockMermaSumPurchased,
        sumSold: stockMerma.stockMermaSumSold,
        sumShrinkage: stockMerma.stockMermaSumShrinkage,
        sumBreakage: stockMerma.stockMermaSumBreakage,
        sumExpiry: stockMerma.stockMermaSumExpiry,
        sumExpiryCost: stockMerma.stockMermaSumExpiryCost,
        sumShrinkageCost: stockMerma.stockMermaSumShrinkageCost,
        page: stockMerma.stockMermaPage,
        pageSize: stockMerma.stockMermaPageSize,
        totalCount: stockMerma.stockMermaTotalCount,
      });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) {
        presentUnexpectedFailure({
          module: 'reports',
          op: 'share',
          error: e,
          title: 'Error',
          userMessage: 'No se pudo exportar la merma.',
        });
      }
    }
  }, [
    canExport,
    navigationPane.dateRangePreset,
    navigationPane.customRangeFrom,
    navigationPane.customRangeTo,
    stockMerma.stockMermaRows,
    stockMerma.stockMermaSumPurchased,
    stockMerma.stockMermaSumSold,
    stockMerma.stockMermaSumShrinkage,
    stockMerma.stockMermaSumBreakage,
    stockMerma.stockMermaSumExpiry,
    stockMerma.stockMermaSumExpiryCost,
    stockMerma.stockMermaSumShrinkageCost,
    stockMerma.stockMermaPage,
    stockMerma.stockMermaPageSize,
    stockMerma.stockMermaTotalCount,
  ]);

  const handleOpenStockToRegisterMerma = useCallback(() => {
    navigationPane.navigation.navigate('Inventory', {
      focusToken: `merma-${Date.now()}`,
    });
  }, [navigationPane.navigation]);

  const handleOpenTesoreriaDetalle = useCallback(() => {
    setReportsNavigationIntent({ section: 'operacion', operacionAnchor: 'tesoreria' });
    navigationPane.setActiveSection('operacion');
  }, [navigationPane.setActiveSection]);

  const handleOpenSupplierInPurchases = useCallback(
    (supplierId: string | null, supplierName: string) => {
      if (!supplierId) return;
      navigationPane.navigation.navigate('Purchases', {
        openSupplierAccountId: supplierId,
        openSupplierAccountName: supplierName,
        focusToken: `informes-supplier-${supplierId}-${Date.now()}`,
      });
    },
    [navigationPane.navigation],
  );

  const loadFn = useCallback(
    async (opts?: { force?: boolean }) => {
      await operacionPane.loadFn(opts);
      setStockMermaRefreshKey((k) => k + 1);
    },
    [operacionPane.loadFn],
  );

  useEffect(() => {
    if (allowedSections.length === 0) return;
    if (!allowedSections.includes(navigationPane.activeSection)) {
      navigationPane.setActiveSection(allowedSections[0]);
    }
  }, [allowedSections, navigationPane.activeSection, navigationPane.setActiveSection]);

  const prevBranchIdRef = useRef<string | undefined>(currentBranchId);
  useEffect(() => {
    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = currentBranchId;
    if (!shouldResetReportsScopeOnBranchChange(prevBranchId, currentBranchId)) return;

    ventasPane.resetVentasFilters();
    operacionPane.clearDashboardCache();
  }, [currentBranchId, ventasPane.resetVentasFilters, operacionPane.clearDashboardCache]);

  return {
    currentBranchId,
    branchesLoading,
    user,
    canEdit,
    canExport,
    canMargins,
    canSales,
    canCash,
    canExpenses,
    canInventory,
    canCustomer,
    canByEmployee,
    canPurchases,
    canAppointments,
    allowedSections,
    activeSection: navigationPane.activeSection,
    setActiveSection: navigationPane.setActiveSection,
    isResumenSection: navigationPane.isResumenSection,
    isVentasSection: navigationPane.isVentasSection,
    isPersonalSection: navigationPane.isPersonalSection,
    isOperacionSection: navigationPane.isOperacionSection,
    isAgendaSection: navigationPane.isAgendaSection,
    dateRangePreset: navigationPane.dateRangePreset,
    setDateRangePreset: navigationPane.setDateRangePreset,
    customRangeFrom: navigationPane.customRangeFrom,
    setCustomRangeFrom: navigationPane.setCustomRangeFrom,
    customRangeTo: navigationPane.customRangeTo,
    setCustomRangeTo: navigationPane.setCustomRangeTo,
    dateRangeLabel: navigationPane.dateRangeLabel,
    customRangeParam: navigationPane.customRangeParam,
    ventasProductFiltersActiveForUi: ventasPane.ventasProductFiltersActiveForUi,
    reportsQueryScope: ventasPane.reportsQueryScope,
    reportsQueryFiltersActive: ventasPane.reportsQueryFiltersActive,
    reportsCustomerFilterId: ventasPane.reportsCustomerFilterId,
    reportsCustomerFilterLabel: ventasPane.reportsCustomerFilterLabel,
    setReportsCustomerFilter: ventasPane.setReportsCustomerFilter,
    clearReportsCustomerFilter: ventasPane.clearReportsCustomerFilter,
    reportsOperatorFilterId: ventasPane.reportsOperatorFilterId,
    reportsOperatorFilterLabel: ventasPane.reportsOperatorFilterLabel,
    setReportsOperatorFilter: ventasPane.setReportsOperatorFilter,
    clearReportsOperatorFilter: ventasPane.clearReportsOperatorFilter,
    reportsCashSessionFilterId: ventasPane.reportsCashSessionFilterId,
    setReportsCashSessionFilterId: ventasPane.setReportsCashSessionFilterId,
    reportsHasDiscountFilter: ventasPane.reportsHasDiscountFilter,
    setReportsHasDiscountFilter: ventasPane.setReportsHasDiscountFilter,
    reportsPromotionFilterId: ventasPane.reportsPromotionFilterId,
    setReportsPromotionFilterId: ventasPane.setReportsPromotionFilterId,
    reportsPromoCodeFilter: ventasPane.reportsPromoCodeFilter,
    setReportsPromoCodeFilter: ventasPane.setReportsPromoCodeFilter,
    personalDashboard: operacionPane.personalDashboard,
    personalOperatorsForRanking: operacionPane.personalOperatorsForRanking,
    personalLoading: operacionPane.personalLoading,
    personalLoadError: operacionPane.personalLoadError,
    loading: operacionPane.loading,
    loadError: operacionPane.loadError,
    loadFn,
    lastDashboardLoadAt: operacionPane.lastDashboardLoadAt,
    branchHealth: operacionPane.branchHealth,
    cashSummary: operacionPane.cashSummary,
    todaySalesSnapshot: operacionPane.todaySalesSnapshot,
    periodSalesSnapshot: operacionPane.periodSalesSnapshot,
    previousPeriodSalesSnapshot: operacionPane.previousPeriodSalesSnapshot,
    salesDailySeries: operacionPane.salesDailySeries,
    salesByProductRows: salesByProduct.salesByProductRows,
    salesByProductTotalCount: salesByProduct.salesByProductTotalCount,
    salesByProductSumQuantity: salesByProduct.salesByProductSumQuantity,
    salesByProductSumLineTotal: salesByProduct.salesByProductSumLineTotal,
    salesByProductUniformUnitOfMeasure: salesByProduct.salesByProductUniformUnitOfMeasure,
    salesByProductSource: salesByProduct.salesByProductSource,
    salesByProductLoading: salesByProduct.salesByProductLoading,
    salesByProductPage: salesByProduct.salesByProductPage,
    setSalesByProductPage: salesByProduct.setSalesByProductPage,
    goToNextSalesByProductPage: salesByProduct.goToNextSalesByProductPage,
    goToPrevSalesByProductPage: salesByProduct.goToPrevSalesByProductPage,
    salesByProductAbcCumulativeBefore: salesByProduct.salesByProductAbcCumulativeBefore,
    salesByProductCategory: ventasPane.salesByProductCategory,
    setSalesByProductCategory: ventasPane.setSalesByProductCategory,
    salesByProductCategoryOptions: ventasPane.salesByProductCategoryOptions,
    salesByProductItemTypeFilter: ventasPane.salesByProductItemTypeFilter,
    setSalesByProductItemTypeFilter: ventasPane.setSalesByProductItemTypeFilter,
    salesByProductSort: salesByProduct.salesByProductSort,
    setSalesByProductSort: salesByProduct.setSalesByProductSort,
    reportsVentasPinnedProductId: ventasPane.reportsVentasPinnedProductId,
    reportsVentasPinnedProductLabel: ventasPane.reportsVentasPinnedProductLabel,
    pinVentasProductFromCatalog: ventasPane.pinVentasProductFromCatalog,
    clearVentasProductPin: ventasPane.clearVentasProductPin,
    reportsVentasBrandFilter: ventasPane.reportsVentasBrandFilter,
    setReportsVentasBrandFilter: ventasPane.setReportsVentasBrandFilter,
    reportsVentasSupplierFilter: ventasPane.reportsVentasSupplierFilter,
    setReportsVentasSupplierFilter: ventasPane.setReportsVentasSupplierFilter,
    reportsVentasBrandOptions: ventasPane.reportsVentasBrandOptions,
    reportsVentasSupplierOptions: ventasPane.reportsVentasSupplierOptions,
    salesByProductPageSize: salesByProduct.salesByProductPageSize,
    personalRankingPageSize: REPORTS_INFORMES_LIST_PAGE_SIZE,
    handleShareSalesByProduct: operacionPane.handleShareSalesByProduct,
    periodRentabilidadKpis: operacionPane.periodRentabilidadKpis,
    previousPeriodRentabilidadKpis: operacionPane.previousPeriodRentabilidadKpis,
    rentabilidadLoading: operacionPane.rentabilidadLoading,
    rentabilidadError: operacionPane.rentabilidadError,
    rentabilidadErrorShowRetry: operacionPane.rentabilidadErrorShowRetry,
    handleLoadRentabilidad: operacionPane.handleLoadRentabilidad,
    salesFiscalCount: operacionPane.salesFiscalCount,
    salesFiscalTotal: operacionPane.salesFiscalTotal,
    salesNonFiscalCount: operacionPane.salesNonFiscalCount,
    salesNonFiscalTotal: operacionPane.salesNonFiscalTotal,
    todaySalesFiscalCount: operacionPane.todaySalesFiscalCount,
    todaySalesFiscalTotal: operacionPane.todaySalesFiscalTotal,
    todaySalesNonFiscalCount: operacionPane.todaySalesNonFiscalCount,
    todaySalesNonFiscalTotal: operacionPane.todaySalesNonFiscalTotal,
    posExceptionSummary: operacionPane.posExceptionSummary,
    inventoryDashboard: operacionPane.inventoryDashboard,
    salesComparison: operacionPane.salesComparison,
    timeBands: operacionPane.timeBands,
    salesByCashier: operacionPane.salesByCashier,
    promotionPerformance: operacionPane.promotionPerformance,
    salesByCashierSource: operacionPane.salesByCashierSource,
    customerSummary: operacionPane.customerSummary,
    customerModuleEnabled,
    paymentMethodNames: operacionPane.paymentMethodNames,
    externalServiceSummary: operacionPane.externalServiceSummary,
    intelligenceData: operacionPane.intelligenceData,
    intelligenceLoading: operacionPane.intelligenceLoading,
    intelligenceError: operacionPane.intelligenceError,
    intelligenceErrorShowRetry: operacionPane.intelligenceErrorShowRetry,
    handleLoadIntelligence: operacionPane.handleLoadIntelligence,
    handleShareSummary: operacionPane.handleShareSummary,
    handleShareVentasSummary: operacionPane.handleShareVentasSummary,
    handleSharePersonalRanking: operacionPane.handleSharePersonalRanking,
    handleShareCompras: operacionPane.handleShareCompras,
    handleShareStockMerma,
    handleOpenStockToRegisterMerma,
    ...stockMerma,
    handleOpenBranchSelector: navigationPane.handleOpenBranchSelector,
    handleOpenMercadoPagoSettings: navigationPane.handleOpenMercadoPagoSettings,
    handleOpenCashWorkspace: navigationPane.handleOpenCashWorkspace,
    kpiSources: operacionPane.kpiSources,
    branchCashSessions: operacionPane.branchCashSessions,
    openBranchSalesHistory: ventasPane.openBranchSalesHistory,
    openBranchSalesHistoryPending: ventasPane.openBranchSalesHistoryPending,
    resetVentasFilters: ventasPane.resetVentasFilters,
    expensesSummary: operacionPane.expensesSummary,
    profitLoss: operacionPane.profitLoss,
    expensesProfitLossLoading: operacionPane.expensesProfitLossLoading,
    treasurySummary: operacionPane.treasurySummary,
    treasuryLoading: operacionPane.treasuryLoading,
    treasuryError: operacionPane.treasuryError,
    reloadTreasury: operacionPane.reloadTreasury,
    handleOpenTesoreriaDetalle,
    consumeOperacionAnchor: navigationPane.consumeOperacionAnchor,
    canShowTreasuryBlock: canView && (canSales || canCash || canExpenses),
    purchasesSummary: comprasPane.purchasesSummary,
    supplierPerformance: comprasPane.supplierPerformance,
    purchasesLoading: comprasPane.purchasesLoading,
    purchasesError: comprasPane.purchasesError,
    reloadPurchases: comprasPane.reloadPurchases,
    handleOpenSupplierInPurchases,
    refreshing: false as boolean,
    onRefresh: (() => {}) as () => void,
  };
};

export type ReportsScreenViewModel = ReturnType<typeof useReportsScreen>;
